import Vue from 'vue'
import Downloader from '@/domain/core/utils/downloader'
import service from './service'

const PartStatus = Object.freeze({
    PENDING: 'pending',
    DOWNLOADING: 'downloading',
    STARTED: 'started',
    DONE: 'done',
    FAILED: 'failed',
})

const ArchiveStatus = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    NOT_FOUND: 'not_found',
    EXPIRED: 'expired',
    ERROR: 'error',
})

const DownloadMode = Object.freeze({
    FETCH_PROGRESS: 'fetch_progress',
    BROWSER_MANAGED: 'browser_managed',
})

const MAX_CONCURRENT_DOWNLOADS = 1
const BLOB_URL_REVOKE_DELAY_MS = 60000
const MIN_DOWNLOAD_ANIMATION_MS = 800
const INDETERMINATE_PROGRESS_CAP = 90
const BROWSER_MANAGED_DOWNLOAD_START_DELAY_MS = 800

// Fetch keeps ZIP chunks in JS memory and then creates a Blob, so peak usage can
// be close to 2x the archive size. Keep real byte progress for modest archives
// only; larger or unknown-size archives are handed to the browser download stack.
const FETCH_PROGRESS_MAX_BYTES = 150 * 1024 * 1024

function buildPart(rawPart, index) {
    return {
        index,
        url: rawPart.url,
        filename: rawPart.filename || `archive-${index + 1}.zip`,
        fileCount: rawPart.file_count || 0,
        sizeBytes: rawPart.size_bytes || 0,
        status: PartStatus.PENDING,
        downloadMode: null,
        percentage: 0,
        error: null,
    }
}

function isPartHandled(part) {
    return part.status === PartStatus.DONE || part.status === PartStatus.STARTED
}

function parseContentLength(contentLength) {
    const parsed = contentLength ? parseInt(contentLength, 10) : 0
    return Number.isFinite(parsed) ? parsed : 0
}

function canUseFetchProgress(part) {
    return typeof fetch === 'function' &&
        part.sizeBytes > 0 &&
        part.sizeBytes <= FETCH_PROGRESS_MAX_BYTES
}

class BrowserManagedFallbackError extends Error {
    constructor(message) {
        super(message)
        this.name = 'BrowserManagedFallbackError'
        this.browserManagedFallback = true
    }
}

const state = {
    status: ArchiveStatus.IDLE,
    taskReference: null,
    parts: [],
    expiresAt: null,
    totalFileCount: 0,
    totalSizeBytes: 0,
    failedCount: 0,
    errorMessage: null,
    downloadActive: false,
}

const getters = {
    status: state => state.status,
    parts: state => state.parts,
    taskReference: state => state.taskReference,
    expiresAt: state => state.expiresAt,
    totalFileCount: state => state.totalFileCount,
    totalSizeBytes: state => state.totalSizeBytes,
    failedCount: state => state.failedCount,
    errorMessage: state => state.errorMessage,
    downloadActive: state => state.downloadActive,
    overallPercentage: state => {
        if (!state.parts.length) return 0
        const sum = state.parts.reduce((acc, p) => acc + (p.percentage || 0), 0)
        return Math.round(sum / state.parts.length)
    },
    completedPartsCount: state => state.parts.filter(p => p.status === PartStatus.DONE).length,
    handledPartsCount: state => state.parts.filter(isPartHandled).length,
    isReady: state => state.status === ArchiveStatus.READY,
    isAllDownloaded: state => state.parts.length > 0 &&
        state.parts.every(p => p.status === PartStatus.DONE),
    isAllHandled: state => state.parts.length > 0 && state.parts.every(isPartHandled),
    hasBrowserManagedDownloads: state => state.parts.some(p =>
        p.downloadMode === DownloadMode.BROWSER_MANAGED || p.status === PartStatus.STARTED),
    isAnyDownloading: state => state.parts.some(p => p.status === PartStatus.DOWNLOADING),
}

const mutations = {
    SET_STATUS(state, status) {
        state.status = status
    },
    SET_TASK_REFERENCE(state, reference) {
        state.taskReference = reference
    },
    SET_ARCHIVE_DATA(state, {parts, expiresAt, totalFileCount, totalSizeBytes, failedCount}) {
        state.parts = parts
        state.expiresAt = expiresAt
        state.totalFileCount = totalFileCount
        state.totalSizeBytes = totalSizeBytes
        state.failedCount = failedCount
    },
    SET_ERROR_MESSAGE(state, message) {
        state.errorMessage = message
    },
    SET_DOWNLOAD_ACTIVE(state, active) {
        state.downloadActive = active
    },
    UPDATE_PART(state, {index, ...updates}) {
        const part = state.parts[index]
        if (!part) return
        Vue.set(state.parts, index, {...part, ...updates})
    },
}

function saveBlobAs(blob, filename) {
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    link.rel = 'noopener'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_REVOKE_DELAY_MS)
}

async function downloadPartWithFetchProgress(part, onProgress) {
    const response = await fetch(part.url)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const total = parseContentLength(response.headers.get('content-length')) || part.sizeBytes
    if (total > FETCH_PROGRESS_MAX_BYTES) {
        throw new BrowserManagedFallbackError('Archive is too large for in-browser progress')
    }

    if (!response.body || total === 0) {
        const blob = await response.blob()
        saveBlobAs(blob, part.filename)
        return
    }

    const reader = response.body.getReader()
    const chunks = []
    let received = 0
    let doneReading = false

    while (!doneReading) {
        const {done, value} = await reader.read()
        doneReading = done
        if (done) continue

        chunks.push(value)
        received += value.byteLength || value.length
        if (received > FETCH_PROGRESS_MAX_BYTES) {
            await reader.cancel()
            throw new BrowserManagedFallbackError('Archive exceeded the in-browser progress limit')
        }
        onProgress(Math.min(99, Math.round((received / total) * 100)))
    }

    saveBlobAs(new Blob(chunks), part.filename)
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function nextFrame() {
    return new Promise(resolve => requestAnimationFrame(resolve))
}

function shouldUseBrowserManagedFallback(error) {
    return error instanceof BrowserManagedFallbackError ||
        error?.browserManagedFallback === true ||
        error?.name === 'TypeError'
}

async function processBrowserManagedPart(commit, part) {
    commit('UPDATE_PART', {
        index: part.index,
        status: PartStatus.DOWNLOADING,
        downloadMode: DownloadMode.BROWSER_MANAGED,
        percentage: 0,
        error: null,
    })

    try {
        Downloader.downloadFromUrl(part.url, part.filename)
        await sleep(BROWSER_MANAGED_DOWNLOAD_START_DELAY_MS)
        commit('UPDATE_PART', {index: part.index, status: PartStatus.STARTED, percentage: 100})
    } catch (error) {
        commit('UPDATE_PART', {
            index: part.index,
            status: PartStatus.FAILED,
            downloadMode: null,
            percentage: 0,
            error: error?.message || 'Download failed',
        })
    }
}

async function processFetchProgressPart(commit, part) {
    commit('UPDATE_PART', {
        index: part.index,
        status: PartStatus.DOWNLOADING,
        downloadMode: DownloadMode.FETCH_PROGRESS,
        percentage: 0,
        error: null,
    })

    const startedAt = performance.now()
    let realPercentage = 0
    let hasRealProgress = false
    let realComplete = false
    let realError = null

    const downloadTask = (async () => {
        try {
            await downloadPartWithFetchProgress(part, (p) => {
                realPercentage = p
                hasRealProgress = true
            })
            realPercentage = 100
        } catch (error) {
            realError = error
        } finally {
            realComplete = true
        }
    })()

    let lastShown = -1
    let shouldAnimate = true
    while (shouldAnimate) {
        const elapsed = performance.now() - startedAt
        const timeProgress = Math.min(100, (elapsed / MIN_DOWNLOAD_ANIMATION_MS) * 100)
        const displayed = realComplete || hasRealProgress
            ? Math.min(realPercentage, timeProgress)
            : Math.min(timeProgress, INDETERMINATE_PROGRESS_CAP)
        const rounded = Math.round(displayed)

        if (rounded !== lastShown) {
            commit('UPDATE_PART', {index: part.index, percentage: rounded})
            lastShown = rounded
        }

        shouldAnimate = !(realComplete && timeProgress >= 100)
        if (!shouldAnimate) continue
        await nextFrame()
    }

    await downloadTask

    if (realError) {
        return {ok: false, error: realError}
    }

    commit('UPDATE_PART', {index: part.index, status: PartStatus.DONE, percentage: 100})
    return {ok: true}
}

async function processPart(commit, part) {
    const useFetchProgress = canUseFetchProgress(part)
    if (!useFetchProgress) {
        await processBrowserManagedPart(commit, part)
        return
    }

    const result = await processFetchProgressPart(commit, part)
    if (result.ok) return

    if (shouldUseBrowserManagedFallback(result.error)) {
        await processBrowserManagedPart(commit, part)
        return
    }

    commit('UPDATE_PART', {
        index: part.index,
        status: PartStatus.FAILED,
        downloadMode: null,
        percentage: 0,
        error: result.error?.message || 'Download failed',
    })
}

const actions = {
    async fetchArchive({commit}, taskReference) {
        commit('SET_TASK_REFERENCE', taskReference)
        commit('SET_STATUS', ArchiveStatus.LOADING)
        commit('SET_ERROR_MESSAGE', null)

        try {
            const data = await service.fetchArchive(taskReference)
            const parts = (data.parts || []).map(buildPart)
            commit('SET_ARCHIVE_DATA', {
                parts,
                expiresAt: data.expires_at || null,
                totalFileCount: data.total_file_count || parts.reduce((acc, p) => acc + p.fileCount, 0),
                totalSizeBytes: data.total_size_bytes || parts.reduce((acc, p) => acc + p.sizeBytes, 0),
                failedCount: data.failed_count || 0,
            })
            commit('SET_STATUS', ArchiveStatus.READY)
        } catch (error) {
            const status = error?.response?.status
            const code = error?.response?.data?.error?.code

            if (status === 404 || code === 'ARCHIVE_NOT_FOUND') {
                commit('SET_STATUS', ArchiveStatus.NOT_FOUND)
            } else if (status === 410 || code === 'ARCHIVE_EXPIRED') {
                commit('SET_STATUS', ArchiveStatus.EXPIRED)
            } else {
                commit('SET_STATUS', ArchiveStatus.ERROR)
                commit('SET_ERROR_MESSAGE', error?.response?.data?.error?.message || error?.message || null)
            }
        }
    },

    async startDownload({commit, state}) {
        if (state.downloadActive || !state.parts.length) return
        commit('SET_DOWNLOAD_ACTIVE', true)

        state.parts.forEach((part, index) => {
            if (!isPartHandled(part)) {
                commit('UPDATE_PART', {
                    index,
                    status: PartStatus.PENDING,
                    downloadMode: null,
                    percentage: 0,
                    error: null,
                })
            }
        })

        const queue = state.parts.filter(p => !isPartHandled(p)).map(p => p.index)

        const runWorker = async () => {
            let hasQueuedParts = queue.length > 0
            while (hasQueuedParts) {
                const index = queue.shift()
                if (index === undefined) return
                await processPart(commit, state.parts[index])
                hasQueuedParts = queue.length > 0
            }
        }

        const workers = Array.from({length: MAX_CONCURRENT_DOWNLOADS}, () => runWorker())
        await Promise.all(workers)

        commit('SET_DOWNLOAD_ACTIVE', false)
    },

    async downloadPart({commit, state}, index) {
        const part = state.parts[index]
        if (!part || part.status === PartStatus.DOWNLOADING || isPartHandled(part)) return
        await processPart(commit, part)
    },
}

export {PartStatus, ArchiveStatus}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
